import React, { useCallback, useMemo, useRef, useState } from 'react';
import { levelsQuery } from '~/util/queries/public/levels';

import { Button } from '~/primitives/button';
import { ScrollArea } from '~/primitives/scroll-area';
import { Checkbox } from '~/primitives/checkbox';
import { NumberInput } from '~/primitives/input';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/primitives/dialog';

import Paginator from '~/components/Paginator';
import Search from '~/partials/Search';
import {
    cn,
    getFullLevelThumbnail,
    getLevelDisplayName,
    getLevelDisplayPrefix,
    seo,
} from '~/util';
import { PlacedLevel } from '~/util/types/levels';
import { Filter as FilterIcon } from 'lucide-react';
import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import {
    SpinWheel,
    SpinWheelHandle,
    SpinWheelItem,
    WheelPointer,
} from '~/routes/games/wheel-spinner/-SpinWheel';
import {
    wheelSpinnerStore,
    useWheelSpinnerStore,
    WheelSpinnerState,
} from '~/util/stores/wheel-spinner';
import {
    defaultWheelSpinnerParams,
    wheelSpinnerParamsSchema,
    wheelSpinnerParamsType,
} from './-SearchParams';
import { Tag } from '~/util/types/tags';
import { NlwTier } from '~/util/types/nlw';
import WheelSpinnerFiltersModal from './-WheelSpinnerFilters';
import { useSyncStoreAndSearch } from '~/util/hooks/useSynchronizeStoreAndURL';
import { useShallow } from 'zustand/react/shallow';
import confetti from 'canvas-confetti';
import LevelHistory from '../-LevelHistory';

const LEVELS_PER_PAGE = 20;
const WHEEL_SPIN_DURATION_MS = 10000;
const WHEEL_SPIN_REVOLUTIONS = 6;

export const Route = createFileRoute('/games/wheel-spinner')({
    validateSearch: wheelSpinnerParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultWheelSpinnerParams)],
    },
    loader: async ({ context: { queryClient } }) => {
        return (await queryClient.fetchQuery(
            levelsQuery({
                list: 'classic',
                exclude_removed: true,
            }),
        )) as PlacedLevel[];
    },
    head: () => ({
        meta: seo({
            title: 'Wheel Spinner',
            description: 'Select levels, spin the wheel, and let fate decide.',
        }),
    }),
    component: () => <WheelSpinnerPage />,
});

const WheelSpinnerPage: React.FC = () => {
    const { spinHistory, pushLevel, reset } = useWheelSpinnerStore(
        useShallow(({ data: { spinHistory }, pushLevel, reset }) => ({
            spinHistory,
            pushLevel,
            reset,
        })),
    );
    const navigate = Route.useNavigate();
    const levels = Route.useLoaderData();
    const searchQuery = Route.useSearch();
    const { search, selected_levels, filters, sort, reverse } = searchQuery;

    useSyncStoreAndSearch<WheelSpinnerState, wheelSpinnerParamsType>({
        store: wheelSpinnerStore,
        searchQuery,
        defaultSearchQuery: defaultWheelSpinnerParams,
        navigate,
        loadFromStore: (state) => state.data.settings,
        saveToStore: (state, search) => {
            return {
                ...state,
                data: {
                    ...state.data,
                    settings: search,
                },
            };
        },
    });

    const wheelRef = useRef<SpinWheelHandle>(null);

    const [isSpinning, setIsSpinning] = useState(false);
    // Whether a round is in progress, which locks level selection.
    const [isRoundInProgress, setIsRoundInProgress] = useState(false);
    const [winningLevel, setWinningLevel] = useState<PlacedLevel | null>(null);
    const [winningLevelDialogOpen, setWinningLevelDialogOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const levelById = useMemo(
        () => new Map(levels.map((level) => [level.id, level])),
        [levels],
    );

    const spinnedLevels = useMemo(
        () =>
            spinHistory.map(
                ({ id }) =>
                    levelById.get(id) ??
                    ({
                        id,
                        name: 'Unknown Level',
                        level_id: 0,
                        position: 0,
                        two_player: false,
                        status: 'MainList',
                        points: 0,
                        requires_raw_footage: false,
                    } as PlacedLevel),
            ),
        [levelById, spinHistory],
    );

    const selectedLevelById = useMemo(
        () => new Map(selected_levels.map((level) => [level.level, level])),
        [selected_levels],
    );

    const selectedLevels = useMemo(
        () =>
            selected_levels
                .map(({ level: levelId, weight }) => {
                    const level = levelById.get(levelId);
                    return level ? { level, weight } : null;
                })
                .filter(
                    (entry): entry is { level: PlacedLevel; weight: number } =>
                        entry != null,
                ),
        [levelById, selected_levels],
    );

    const sortedAndFilteredLevels = useMemo(() => {
        const selectedTiers = new Set(filters.tiers);

        return levels
            .filter(
                (level) =>
                    level.name.toLowerCase().includes(search.toLowerCase()) &&
                    Object.keys(filters.tags).every(
                        (tag) =>
                            level.tags &&
                            level.tags.includes(tag as Tag) ===
                                filters.tags[tag as Tag],
                    ) &&
                    filters.ranges.every(({ property, range }) => {
                        const value = level[property];
                        if (range.min === undefined && range.max === undefined)
                            return true;
                        if (value == null) return false;
                        return (
                            (range.min !== undefined
                                ? Number(value) >= range.min
                                : true) &&
                            (range.max !== undefined
                                ? Number(value) <= range.max
                                : true)
                        );
                    }) &&
                    (filters.tiers.length === 0 ||
                        selectedTiers.has((level.nlw_tier ?? '') as NlwTier)),
            )
            .sort((levelA, levelB) => {
                const result =
                    sort === 'rank'
                        ? (levelA.position ?? Infinity) -
                          (levelB.position ?? Infinity)
                        : sort === 'enjoyment'
                          ? levelA?.edel_enjoyment && levelB.edel_enjoyment
                              ? levelB.edel_enjoyment - levelA.edel_enjoyment
                              : levelA?.edel_enjoyment
                                ? -1
                                : levelB?.edel_enjoyment
                                  ? 1
                                  : 0
                          : 0;
                return reverse ? -result : result;
            });
    }, [
        levels,
        search,
        filters.tags,
        filters.ranges,
        filters.tiers,
        sort,
        reverse,
    ]);

    const paginatedLevels = useMemo(() => {
        const start = (currentPage - 1) * LEVELS_PER_PAGE;
        return sortedAndFilteredLevels.slice(start, start + LEVELS_PER_PAGE);
    }, [sortedAndFilteredLevels, currentPage]);

    const totalPages = Math.ceil(
        sortedAndFilteredLevels.length / LEVELS_PER_PAGE,
    );

    const removeSelectedLevel = useCallback(
        (levelId: string) => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    selected_levels: prev.selected_levels.filter(
                        (selectedLevel) => selectedLevel.level !== levelId,
                    ),
                }),
                replace: true,
            });
        },
        [navigate],
    );

    const toggleLevel = useCallback(
        (level: PlacedLevel) => {
            if (selectedLevelById.has(level.id)) {
                removeSelectedLevel(level.id);
            } else {
                navigate({
                    search: (prev) => ({
                        ...prev,
                        selected_levels: shuffleSelectedLevels([
                            ...prev.selected_levels,
                            {
                                level: level.id,
                                weight: 1,
                            },
                        ]),
                    }),
                    replace: true,
                });
            }
        },
        [navigate, removeSelectedLevel, selectedLevelById],
    );

    const removeLevel = useCallback(
        (level: PlacedLevel) => {
            removeSelectedLevel(level.id);
        },
        [removeSelectedLevel],
    );

    const setLevelWeight = useCallback(
        (levelId: string, weight: number | null) => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    selected_levels: prev.selected_levels.map((level) =>
                        level.level === levelId
                            ? {
                                  ...level,
                                  weight: weight ?? 1,
                              }
                            : level,
                    ),
                }),
                replace: true,
            });
        },
        [navigate],
    );

    const selectAllLevels = useCallback(
        (levels: PlacedLevel[]) => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    selected_levels: shuffleSelectedLevels(
                        addSelectedLevels(prev.selected_levels, levels),
                    ),
                }),
                replace: true,
            });
        },
        [navigate],
    );

    const showWheelImages = selectedLevels.length <= 30;

    const items = useMemo<SpinWheelItem<PlacedLevel>[]>(
        () =>
            selectedLevels.map(({ level, weight }) => ({
                label: level.name,
                value: level,
                weight: weight ?? 1,
                labelColor: '#ffffff',
                image: showWheelImages
                    ? getFullLevelThumbnail(level.level_id)
                    : null,
            })),
        [selectedLevels, showWheelImages],
    );

    const onSpinResult = useCallback(
        (item: SpinWheelItem<PlacedLevel>) => {
            setIsSpinning(false);
            setWinningLevel(item.value);
            pushLevel(item.value);
            setWinningLevelDialogOpen(true);
            void confetti();
        },
        [pushLevel],
    );

    const onResultDialogOpenChange = useCallback((open: boolean) => {
        setWinningLevelDialogOpen(open);
        if (!open) {
            setIsRoundInProgress(false);
        }
    }, []);

    const spinWheel = useCallback(() => {
        if (!items.length || isSpinning) return;
        const targetIndex = getWeightedRandomItemIndex(items);
        if (targetIndex == null) return;

        setIsRoundInProgress(true);
        setWinningLevel(null);
        setWinningLevelDialogOpen(false);
        wheelRef.current?.spinToItem(
            targetIndex,
            WHEEL_SPIN_DURATION_MS,
            false,
            WHEEL_SPIN_REVOLUTIONS,
            1,
            wheelEasing,
        );
    }, [isSpinning, items]);

    return (
        <div className="mt-8 lg:mt-10 flex flex-col items-center justify-center gap-4">
            <div className="bg-background-dark py-8 px-8 rounded-xl shadow-lg w-full max-w-360 backdrop-blur-md text-center">
                <h1 className="text-5xl font-bold text-secondary-foreground mb-6">
                    WHEEL SPINNER
                </h1>

                <p className="text-lg text-secondary-light mb-6">
                    Select levels, spin the wheel, and let fate decide.
                </p>

                <div className="flex flex-col lg:flex-row justify-center gap-10 w-full">
                    <div className="flex flex-col items-center gap-4 w-full lg:w-1/2">
                        {selectedLevels.length > 0 ? (
                            <div
                                className={cn(
                                    'relative inline-block rounded-full aspect-square w-[min(80vw,31.25rem)] max-w-full',
                                    isRoundInProgress && 'pointer-events-none',
                                )}
                                style={{
                                    boxShadow: '0 0 20px rgba(0, 0, 0, 0.6)',
                                }}
                            >
                                <SpinWheel
                                    ref={wheelRef}
                                    className="w-full h-full"
                                    items={items}
                                    itemLabelFont="Poppins"
                                    itemLabelStrokeColor="rgba(0,0,0,0.6)"
                                    itemLabelStrokeWidth={3}
                                    itemBackgroundColors={[
                                        '#3b3b3b',
                                        '#1f1f1f',
                                    ]}
                                    onSpinStart={() => setIsSpinning(true)}
                                    onSpinResult={onSpinResult}
                                    pointerAngle={90}
                                />

                                <WheelPointer
                                    angle={90}
                                    gap={65}
                                    sizeRatio={0.2}
                                />
                            </div>
                        ) : (
                            <p className="text-secondary-light italic mt-4">
                                Please select at least one level to spin.
                            </p>
                        )}

                        {!isRoundInProgress ? (
                            <Button
                                onClick={spinWheel}
                                disabled={items.length <= 1}
                                className={
                                    'mt-4 px-6 py-3 rounded-lg font-semibold transition-opacity'
                                }
                            >
                                {'Spin the Wheel'}
                            </Button>
                        ) : (
                            <div className="mt-4 h-10 text-sm text-secondary-light">
                                {isSpinning ? 'Spinning...' : ''}
                            </div>
                        )}
                    </div>

                    <div className="w-full lg:w-[40%] max-h-150 flex flex-col gap-4">
                        <div className="bg-background-light p-4 rounded-md h-full border border-border flex flex-col">
                            <div
                                className={`px-1 flex flex-col items-center justify-between gap-3 mb-4 ${
                                    isRoundInProgress
                                        ? 'pointer-events-none opacity-50'
                                        : ''
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <Search
                                        initial_input={search}
                                        onInputChange={(value: string) =>
                                            navigate({
                                                search: (prev) => ({
                                                    ...prev,
                                                    search: value,
                                                }),
                                                replace: true,
                                            })
                                        }
                                    />
                                    <FilterIcon
                                        className="w-8 h-8 text-secondary-light hover:text-secondary-foreground cursor-pointer transition-colors"
                                        onClick={() => setFiltersOpen(true)}
                                    />
                                </div>
                                <div className="flex w-full items-center justify-end gap-3">
                                    <Button
                                        size="sm"
                                        className="text-xs px-4 py-2"
                                        onClick={() => {
                                            if (isRoundInProgress) return;
                                            selectAllLevels(paginatedLevels);
                                        }}
                                    >
                                        Select All
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        className="text-xs px-4 py-2"
                                        onClick={() => {
                                            if (isRoundInProgress) return;
                                            navigate({
                                                search: defaultWheelSpinnerParams,
                                                replace: true,
                                            });
                                        }}
                                    >
                                        Clear All
                                    </Button>
                                </div>
                            </div>
                            <ScrollArea
                                className="mb-2"
                                style={{ height: '425px' }}
                            >
                                <div className="flex flex-col gap-1 pr-2">
                                    {paginatedLevels.map((level) => {
                                        const selectedLevel =
                                            selectedLevelById.get(level.id);
                                        const isSelected =
                                            selectedLevel != null;
                                        return (
                                            <div
                                                key={level.name}
                                                role="button"
                                                tabIndex={0}
                                                className={`flex items-center justify-between px-3 py-1.5 rounded-md transition-colors
                                                        ${
                                                            isRoundInProgress
                                                                ? 'opacity-50 cursor-not-allowed'
                                                                : 'hover:bg-muted cursor-pointer'
                                                        }`}
                                                onClick={() =>
                                                    !isRoundInProgress &&
                                                    toggleLevel(level)
                                                }
                                                onKeyDown={(event) => {
                                                    if (
                                                        (event.key ===
                                                            'Enter' ||
                                                            event.key ===
                                                                ' ') &&
                                                        !isRoundInProgress
                                                    ) {
                                                        event.preventDefault();
                                                        toggleLevel(level);
                                                    }
                                                }}
                                            >
                                                <span
                                                    className={cn(
                                                        'min-w-0 flex-1 truncate text-left text-sm text-secondary-foreground',
                                                        level.status == 'Legacy'
                                                            ? 'opacity-50'
                                                            : '',
                                                    )}
                                                >
                                                    {getLevelDisplayPrefix(
                                                        level,
                                                    )}
                                                    {level.name}
                                                </span>
                                                {isSelected && (
                                                    <div className="ml-2 flex items-center gap-1 text-xs text-secondary-light">
                                                        <p>Weight</p>
                                                        <NumberInput
                                                            value={
                                                                selectedLevel?.weight
                                                            }

                                                            onChange={(value) =>
                                                                setLevelWeight(
                                                                    level.id,
                                                                    value,
                                                                )
                                                            }
                                                            disabled={
                                                                isRoundInProgress
                                                            }
                                                            aria-label={`Weight for ${level.name}`}
                                                            onClick={(event) =>
                                                                event.stopPropagation()
                                                            }
                                                            decimalPlaces={2}
                                                            onKeyDown={(
                                                                event,
                                                            ) =>
                                                                event.stopPropagation()
                                                            }
                                                            className="h-8 w-16 p-2 text-sm text-center"
                                                        />
                                                    </div>
                                                )}
                                                <Checkbox
                                                    checked={isSelected}
                                                    disabled={isRoundInProgress}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </ScrollArea>
                            <div
                                className={
                                    isRoundInProgress
                                        ? 'pointer-events-none opacity-50'
                                        : ''
                                }
                            >
                                <Paginator
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <LevelHistory
                title="Spin History"
                emptyMessage="No spins yet."
                levelHistory={spinnedLevels}
                showPercentage={false}
                showCheckIcon={false}
            />
            {spinHistory.length > 0 && (
                <Button
                    variant="default"
                    className="w-full max-w-180"
                    onClick={reset}
                >
                    Clear Spin History
                </Button>
            )}
            <WheelSpinnerFiltersModal
                isOpen={filtersOpen}
                onClose={() => setFiltersOpen(false)}
            />
            <Dialog
                open={winningLevelDialogOpen && winningLevel != null}
                onOpenChange={onResultDialogOpenChange}
            >
                <DialogContent
                    className="w-lg"
                    style={{
                        backgroundImage: `${winningLevel ? `url(${getFullLevelThumbnail(winningLevel.level_id)})` : 'none'}`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                    aria-describedby={undefined}
                >
                    {winningLevel && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="text-2xl text-shadow-3">
                                    Winner
                                </DialogTitle>
                            </DialogHeader>
                            <h2 className="text-3xl my-4 font-semibold text-white text-shadow-3">
                                {getLevelDisplayName(winningLevel)}
                            </h2>

                            <DialogFooter>
                                <DialogClose asChild>
                                    <Button variant="secondary">Close</Button>
                                </DialogClose>
                                <DialogClose asChild>
                                    <Button
                                        onClick={() => {
                                            setIsRoundInProgress(false);
                                            setWinningLevel(null);
                                            if (winningLevel) {
                                                removeLevel(winningLevel);
                                            }
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </DialogClose>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

function shuffleSelectedLevels<T>(levels: T[]) {
    const shuffled = [...levels];

    for (
        let currentIndex = shuffled.length - 1;
        currentIndex > 0;
        currentIndex--
    ) {
        const randomIndex = Math.floor(Math.random() * (currentIndex + 1));
        [shuffled[currentIndex], shuffled[randomIndex]] = [
            shuffled[randomIndex],
            shuffled[currentIndex],
        ];
    }

    return shuffled;
}

function addSelectedLevels(
    selectedLevels: wheelSpinnerParamsType['selected_levels'],
    levels: PlacedLevel[],
) {
    const selectedIds = new Set(selectedLevels.map(({ level }) => level));
    const nextSelectedLevels = [...selectedLevels];

    for (const level of levels) {
        if (!selectedIds.has(level.id)) {
            nextSelectedLevels.push({
                level: level.id,
                weight: 1,
            });
        }
    }

    return nextSelectedLevels;
}

function getWeightedRandomItemIndex(items: SpinWheelItem<PlacedLevel>[]) {
    const totalWeight = items.reduce(
        (total, item) => total + (item.weight ?? 1),
        0,
    );

    if (totalWeight <= 0) return null;

    let randomTicket = Math.random() * totalWeight;
    for (const [index, item] of items.entries()) {
        randomTicket -= item.weight ?? 1;
        if (randomTicket < 0) return index;
    }

    return items.length - 1;
}

function wheelEasing(progress: number) {
    return 1 - Math.pow(1 - progress, 2) * Math.exp(-0.8 * progress);
}
